import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import config from '../../../config/config';
import { useToast } from '../../../shared/contexts/ToastContext';
import { loadCart, saveCart } from '../utils/cartStorage';
import type {
  ShopCartContextValue,
  ShopCartLine,
  ShopCartState,
  ShopProductDetail,
  ShopProductSummary,
} from '../types';

const CartContext = createContext<ShopCartContextValue>({
  lines: [],
  lineCount: 0,
  itemCount: 0,
  subtotal: 0,
  addLine: () => {},
  setQty: () => {},
  removeLine: () => {},
  clear: () => {},
  isInCart: () => false,
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
});

export const useCart = (): ShopCartContextValue => useContext(CartContext);

interface CartProviderProps {
  children: ReactNode;
}

const clampQty = (qty: number): number => {
  if (!Number.isFinite(qty)) return 1;
  return Math.min(Math.max(Math.trunc(qty), 1), config.shop.maxLineQty);
};

/**
 * Cart state lives entirely in the browser — the shop API has no cart or
 * account endpoints. Persisted to localStorage with no expiry.
 */
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [state, setState] = useState<ShopCartState>(() => loadCart());
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    saveCart(state);
  }, [state]);

  const addLine = useCallback(
    (product: ShopProductSummary | ShopProductDetail, qty: number = 1) => {
      const wanted = clampQty(qty);

      setState((prev) => {
        const existing = prev.lines.find((line) => line.productId === product.id);

        if (existing) {
          const merged = clampQty(existing.qty + wanted);
          if (merged === existing.qty) {
            showToast(
              t('shop.cart.maxQty', { name: product.name }),
              'warning'
            );
            return prev;
          }
          showToast(t('shop.cart.updated', { name: product.name }), 'success');
          openDrawer();
          return {
            ...prev,
            lines: prev.lines.map((line) =>
              line.productId === product.id ? { ...line, qty: merged } : line
            ),
          };
        }

        if (prev.lines.length >= config.shop.maxCartLines) {
          showToast(
            t('shop.cart.maxLines', { count: config.shop.maxCartLines }),
            'warning'
          );
          return prev;
        }

        const newLine: ShopCartLine = {
          productId: product.id,
          slug: product.slug,
          name: product.name,
          image: product.image ?? product.images?.[0] ?? null,
          unitPrice: product.price,
          currency: product.currency || 'MAD',
          qty: wanted,
          addedAt: new Date().toISOString(),
        };

        showToast(t('shop.cart.added', { name: product.name }), 'success');
        openDrawer();
        return { ...prev, lines: [...prev.lines, newLine] };
      });
    },
    [showToast, t, openDrawer]
  );

  const setQty = useCallback(
    (productId: string, qty: number) => {
      const next = clampQty(qty);
      setState((prev) => ({
        ...prev,
        lines: prev.lines.map((line) =>
          line.productId === productId ? { ...line, qty: next } : line
        ),
      }));
    },
    []
  );

  const removeLine = useCallback((productId: string) => {
    setState((prev) => ({
      ...prev,
      lines: prev.lines.filter((line) => line.productId !== productId),
    }));
  }, []);

  const clear = useCallback(() => {
    setState({ version: 1, lines: [] });
  }, []);

  const isInCart = useCallback(
    (productId: string) => state.lines.some((line) => line.productId === productId),
    [state.lines]
  );

  const value = useMemo<ShopCartContextValue>(() => {
    const itemCount = state.lines.reduce((sum, line) => sum + line.qty, 0);
    const subtotal = state.lines.reduce(
      (sum, line) => sum + line.unitPrice * line.qty,
      0
    );
    return {
      lines: state.lines,
      lineCount: state.lines.length,
      itemCount,
      subtotal,
      addLine,
      setQty,
      removeLine,
      clear,
      isInCart,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
    };
  }, [
    state.lines,
    addLine,
    setQty,
    removeLine,
    clear,
    isInCart,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
